// Compact horizontal game-state strip shown ONLY in fullscreen recording mode,
// where the right-hand HudRail is hidden to give the video full width. Mirrors
// HudRail's game-state data, but as a single glanceable row pinned to the top.
// Also mirrors HudRail's click-coordinate/timer readout (the HudRail card is
// hidden in fullscreen too, so this is the only place to see it while recording).
// Presentational only — fully unit-testable.

import { parseDecimal } from "../utils";

const STATUS_DOT = {
  connected: "#34d399",
  unmatched: "#f59e0b",
  disconnected: "#f87171",
};

export const HudStrip = ({
  status = "disconnected",
  videoTime = "00:00:00",
  gameState = null,
  gameStateError = false,
  x = null,
  y = null,
  startSecond = null,
  endSecond = null,
  pickoffFeedback = null,
}) => {
  const S = {
    strip: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16, padding: "6px 16px", background: "#0f172a", borderBottom: "1px solid #1e293b", color: "#e2e8f0", fontSize: 14, lineHeight: 1.2 },
    dot: { width: 9, height: 9, borderRadius: "50%", background: STATUS_DOT[status] || STATUS_DOT.disconnected, flexShrink: 0 },
    vt: { fontWeight: 800, fontVariantNumeric: "tabular-nums", fontSize: 18, color: "#f1f5f9" },
    label: { color: "#a0b1c8", marginRight: 6 },
    val: { fontWeight: 700, color: "#f1f5f9", fontVariantNumeric: "tabular-nums" },
    sep: { color: "#334155" },
    muted: { color: "#a0b1c8" },
    error: { color: "#f87171" },
    pickoff: { color: "#93c5fd", fontWeight: 700 },
  };
  const seg = (label, value) => (
    <span>{label && <span style={S.label}>{label}</span>}<span style={S.val}>{value ?? "—"}</span></span>
  );
  const timerText = startSecond === null ? "未設置開始時間" : (endSecond === null ? "已設置開始時間" : `時間區間 = ${parseDecimal(endSecond - startSecond)}`);

  return (
    <div className="hud-strip" style={S.strip}>
      <span className="hud-strip-dot" style={S.dot} />
      <span style={S.vt}>{videoTime}</span>
      <span style={S.sep}>|</span>
      <span className="hud-strip-coord">
        點擊座標 x: {x == null ? "NA" : parseDecimal(x - 45)} y: {y == null ? "NA" : parseDecimal(74.22 - y)}
      </span>
      <span style={S.sep}>|</span>
      <span className="hud-strip-timer">計時器：{timerText}</span>
      {gameState ? (
        <>
          <span style={S.sep}>|</span>
          {seg(null, `${gameState.inning} 局${gameState.inning_half === "bottom" ? "下" : "上"}`)}
          <span style={S.sep}>|</span>
          {seg("B-S-O", `${gameState.balls}-${gameState.strikes}-${gameState.outs}`)}
          <span style={S.sep}>|</span>
          {seg("打者", gameState.batter?.name)}
          <span style={S.sep}>|</span>
          {seg("投手", gameState.pitcher?.name)}
          <span style={S.sep}>|</span>
          {seg("投手球數", gameState.pitcher_pitches)}
          {gameStateError && <span style={S.error}>現況無法更新</span>}
        </>
      ) : (
        <span style={gameStateError ? S.error : S.muted}>{gameStateError ? "現況無法取得" : "· 尚未連結賽事"}</span>
      )}
      {pickoffFeedback && (
        <>
          <span style={S.sep}>|</span>
          <span className="hud-strip-pickoff" style={S.pickoff}>已記錄牽制 @ {pickoffFeedback}</span>
        </>
      )}
    </div>
  );
};
